import { type ReactNode, memo } from 'react';
import { IconCheck } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { BrandLogo } from '@/components/common/BrandLogo';
import splitStyles from '@/components/layout/SplitScreenLayout/SplitScreenLayout.module.css';
import type { InvitationStep } from '@/hooks/useInvitationFlow';
import type { GetInvitationByTokenResponse } from '@/types/api/responses';
import { InviterCard } from './InviterCard';
import styles from '../InvitationPage.module.css';

interface InvitationLeftPanelProps {
  step: InvitationStep;
  invitation: GetInvitationByTokenResponse | null;
}

const BADGE_ICON_SIZE = 14;

export const InvitationLeftPanel = memo(function InvitationLeftPanel({
  step,
  invitation,
}: InvitationLeftPanelProps): ReactNode {
  const { t } = useTranslation();

  if (step === 'profile' && invitation) {
    return (
      <>
        <div className={`${splitStyles.fadeElement} ${splitStyles.fadeDelay0}`}>
          <BrandLogo linkToHome />
        </div>

        <div className={styles.leftCenterBlock}>
          <div
            className={`${styles.profileBadge} ${splitStyles.fadeElement} ${splitStyles.fadeDelay1}`}
          >
            <IconCheck size={BADGE_ICON_SIZE} />
            {t('invitation.profile.badge')}
          </div>
          <h1
            className={`${styles.leftHeading} ${splitStyles.fadeElement} ${splitStyles.fadeDelay2}`}
          >
            {t('invitation.profile.heading')}
          </h1>
          <p
            className={`${styles.leftDescription} ${splitStyles.fadeElement} ${splitStyles.fadeDelay3}`}
          >
            {t('invitation.profile.subtitle')}{' '}
            <span className={styles.brandAccent}>
              {invitation.workspaceName}
            </span>
          </p>
        </div>

        <p
          className={`${styles.leftFooter} ${splitStyles.fadeElement} ${splitStyles.fadeDelay4}`}
        >
          {t('invitation.profile.securityNotice')}
        </p>
      </>
    );
  }

  return (
    <>
      <div className={`${splitStyles.fadeElement} ${splitStyles.fadeDelay0}`}>
        <BrandLogo />
      </div>

      <div className={styles.leftCenterBlock}>
        <p
          className={`${styles.eyebrow} ${splitStyles.fadeElement} ${splitStyles.fadeDelay1}`}
        >
          {t('invitation.eyebrow')}
        </p>
        <h1
          className={`${styles.leftHeading} ${splitStyles.fadeElement} ${splitStyles.fadeDelay1}`}
        >
          {t('invitation.heading')}
          <br />
          <span className={styles.brandAccent}>
            {invitation?.workspaceName ?? ''}
          </span>
        </h1>
        <p
          className={`${styles.leftDescription} ${splitStyles.fadeElement} ${splitStyles.fadeDelay2}`}
        >
          {t('invitation.description')}
        </p>
        {invitation && (
          <div
            className={`${splitStyles.fadeElement} ${splitStyles.fadeDelay3}`}
          >
            <InviterCard
              inviterName={invitation.inviterName}
              inviterRole={invitation.inviterRole}
              memberCount={invitation.memberCount}
            />
          </div>
        )}
      </div>

      <p
        className={`${styles.leftFooter} ${splitStyles.fadeElement} ${splitStyles.fadeDelay4}`}
      >
        {t('invitation.expiryNotice')}
      </p>
    </>
  );
});
